import { Link } from "react-router-dom";

const TIMELINE = [
  { y: "1978", t: "A loom in Bhadohi", d: "Iryaa's founding grandmother sets up a single loom in a courtyard in Bhadohi." },
  { y: "1994", t: "The wood workshop", d: "A cousin's carpentry studio joins the family — teak frames enter the atelier." },
  { y: "2011", t: "First international shipment", d: "A silk carpet leaves for a home in Antwerp. The world quietly notices." },
  { y: "2019", t: "The children's line", d: "Kids' baskets born from a request from a young mother in Bengaluru." },
  { y: "2026", t: "The Custom Studio", d: "AI-assisted design meets four generations of hands." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="overline">The Atelier</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl font-light text-[#1B3B36] leading-[1.02] tracking-tight">
            Four generations. <em className="italic text-[#84968C]">One loom.</em>
          </h1>
          <p className="mt-8 text-[16px] leading-relaxed text-[#4A4A4A] font-light max-w-xl">
            Iryaa Homes began in 1978 with a single upright loom in a courtyard
            in Bhadohi, Uttar Pradesh. Nearly five decades on, the atelier is
            still small on purpose — 46 artisans, a handful of designers, no
            factory floor. Every piece we make must still pass under our
            grandmother's approving eyes.
          </p>
        </div>
        <div className="lg:col-span-6">
          <img
            src="https://images.pexels.com/photos/29848182/pexels-photo-29848182.jpeg?auto=compress&cs=tinysrgb&w=1400"
            alt="Iryaa artisan"
            className="w-full aspect-[4/5] object-cover"
          />
        </div>
      </section>

      <section className="bg-[#EFECE5]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">A short history</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light text-[#1B3B36] leading-[1.05]">
              Our story, told slowly.
            </h2>
          </div>
          <ol className="lg:col-span-8 space-y-10">
            {TIMELINE.map((e) => (
              <li key={e.y} className="grid grid-cols-12 gap-6 border-t border-[#1B3B36]/20 pt-6">
                <div className="col-span-3 md:col-span-2 font-serif text-4xl text-[#1B3B36]">
                  {e.y}
                </div>
                <div className="col-span-9 md:col-span-10">
                  <div className="font-serif text-2xl text-[#1B3B36]">{e.t}</div>
                  <p className="text-[14px] mt-2 leading-relaxed text-[#4A4A4A] font-light max-w-2xl">
                    {e.d}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          <img
            src="https://images.pexels.com/photos/37877827/pexels-photo-37877827.jpeg?auto=compress&cs=tinysrgb&w=900"
            alt="Vegetable dyes"
            className="w-full aspect-[3/4] object-cover"
          />
          <img
            src="https://images.unsplash.com/photo-1724582586529-62622e50c0b3?auto=format&fit=crop&w=900&q=80"
            alt="Loom detail"
            className="w-full aspect-[3/4] object-cover mt-12"
          />
        </div>
        <div className="lg:col-span-6">
          <div className="overline">Values</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light text-[#1B3B36] leading-[1.05]">
            The things we will never rush.
          </h2>
          <div className="mt-10 space-y-8">
            {[
              { t: "Slowness", d: "A silk carpet takes 90–120 days. That is exactly how long it should take." },
              { t: "Fair looms", d: "Every artisan earns above the state weaving guild's benchmark, always." },
              { t: "Nothing wasted", d: "Yarn ends become kids' basket trims, wood offcuts become photo frame corners." },
              { t: "Signed forever", d: "Each piece is signed and numbered — traceable to a person, a village, a year." },
            ].map((v) => (
              <div key={v.t} className="border-b border-[#D8D4CC] pb-6">
                <div className="font-serif text-2xl text-[#1B3B36]">{v.t}</div>
                <p className="text-[14px] mt-2 leading-relaxed text-[#4A4A4A] font-light max-w-lg">
                  {v.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1B3B36] text-[#F9F8F6] grain">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <div className="overline text-[#84968C]">Visit us</div>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light leading-[1.05] max-w-3xl mx-auto">
            The studio is open by appointment, and always with a cup of chai.
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-visit"
            className="mt-10 inline-flex items-center gap-3 border border-[#F9F8F6] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#F9F8F6] hover:text-[#1B3B36] transition-colors"
          >
            Book a visit
          </Link>
        </div>
      </section>
    </div>
  );
}
